import { useCallback, useEffect, useState } from 'react';

// Persisted "recently used" color history for the editor's color menus.
// Each menu (text color / highlight / cell background) keeps its OWN history
// under a distinct storage key — the vocabularies don't overlap (text colors
// are saturated, highlight/cell bgs are light tints), so mixing them in one
// list would just be confusing.
//
// The list is capped + dedup'd, newest first. Picking a color that's already
// in the list re-promotes it to the front rather than creating a duplicate
// entry. localStorage failures (private mode, quota) silently fall back to an
// in-memory only history — the user still sees recents this session, they
// just won't survive a reload.
//
// REACTIVITY: the same key is consumed by more than one mounted menu at once
// (e.g. the persistent PlateToolbar AND the transient FloatingToolbar). A plain
// useState that only reads on mount would let those instances drift — a color
// pushed from the top toolbar wouldn't appear in an already-mounted floating
// menu, and vice-versa. We broadcast a same-tab CustomEvent on every push (and
// also listen to the cross-tab `storage` event) so EVERY mounted instance for
// the same key re-reads and stays in sync.

const MAX_RECENT = 6;
const RECENT_COLORS_EVENT = 'plate:recent-colors-changed';

function readRecents(storageKey: string): string[] {
    if (typeof window === 'undefined') return [];
    try {
        const raw = window.localStorage.getItem(storageKey);
        if (!raw) return [];
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        return arr.filter((c): c is string => typeof c === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(c)).slice(0, MAX_RECENT);
    } catch {
        return [];
    }
}

export function useRecentColors(storageKey: string) {
    const [recents, setRecents] = useState<string[]>(() => readRecents(storageKey));

    // Keep this instance in sync with any other menu sharing the same key,
    // and with the latest localStorage value (re-read on broadcast / storage).
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const resync = () => setRecents(readRecents(storageKey));
        const onCustom = (e: Event) => {
            if ((e as CustomEvent).detail === storageKey) resync();
        };
        const onStorage = (e: StorageEvent) => {
            if (e.key === storageKey) resync();
        };
        // Re-read once on (re)mount too, in case localStorage changed while the
        // instance was unmounted (the FloatingToolbar mounts/unmounts a lot).
        resync();
        window.addEventListener(RECENT_COLORS_EVENT, onCustom as EventListener);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener(RECENT_COLORS_EVENT, onCustom as EventListener);
            window.removeEventListener('storage', onStorage);
        };
    }, [storageKey]);

    const pushRecent = useCallback(
        (color: string | undefined | null) => {
            if (!color || typeof color !== 'string') return;
            const normalized = color.toLowerCase();
            // Skip non-hex inputs (e.g. 'transparent', named colors) — the
            // native color picker only emits hex, but defensive guard keeps
            // the stored shape consistent.
            if (!/^#[0-9a-f]{3,8}$/.test(normalized)) return;
            // Compute the next list from the PERSISTED value (source of truth),
            // and write it SYNCHRONOUSLY — NOT inside the setRecents updater.
            // The FloatingToolbar tears its color menu down the moment the
            // eyedropper / OS overlay opens, so the component can unmount before
            // React flushes a state updater; doing the localStorage write here
            // guarantees the recent persists regardless. The broadcast then
            // refreshes any still-mounted sibling menu (top + floating).
            const prev = readRecents(storageKey);
            const filtered = prev.filter((c) => c.toLowerCase() !== normalized);
            const next = [normalized, ...filtered].slice(0, MAX_RECENT);
            try {
                window.localStorage.setItem(storageKey, JSON.stringify(next));
                window.dispatchEvent(new CustomEvent(RECENT_COLORS_EVENT, { detail: storageKey }));
            } catch {
                /* private mode / quota — recents stay in-memory only this session */
            }
            setRecents(next);
        },
        [storageKey],
    );

    return [recents, pushRecent] as const;
}
